<template>
  <div class="salas-tempo-real">
    <PainelHeader />

    <main class="salas-tempo-real__content">
      <button type="button" class="salas-tempo-real__back" @click="goBack">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M10 3L5 8l5 5V3z"/>
        </svg>
        Voltar
      </button>

      <section class="salas-tempo-real__panel">
        <PainelSearchFilter
          name="salas"
          :search.sync="search"
          :selected-cliente-id.sync="selectedClienteId"
          :is-admin="isAdmin"
          :clientes="clientes"
          :refreshing="manualRefreshing"
          @cliente-change="onClienteChange"
          @refresh="refreshSalas"
        />

        <div v-if="loading" class="salas-tempo-real__state">Carregando salas...</div>
        <div v-else-if="loadFailed" class="salas-tempo-real__state salas-tempo-real__state--error">
          <p>Não foi possível carregar as salas.</p>
          <button type="button" @click="loadSalas">Tentar novamente</button>
        </div>

        <PainelSalasTable
          v-else
          :rows="filteredRows"
          :columns="tableColumns"
        />
      </section>
    </main>

    <PainelConfirmModal
      :aberto="confirmModal.aberto"
      :mensagem="confirmModal.mensagem"
      titulo="Desativar sala"
      confirmar-texto="Desativar"
      confirmar-texto-loading="Desativando..."
      variant="danger"
      :loading="desativandoId !== null"
      @confirmar="confirmarDesativar"
      @cancelar="fecharConfirmModal"
    />
  </div>
</template>

<script>
import PainelHeader from '../components/PainelHeader.vue';
import PainelSearchFilter from '../components/PainelSearchFilter.vue';
import PainelConfirmModal from '../components/PainelConfirmModal.vue';
import PainelSalasTable from '../components/PainelSalasTable.vue';
import salasService from '../service/salas.service';
import { listarClientes } from '../service/clientes.service';
import { getClienteId, isAdminCliente, logout } from '../service/auth.service';
import { mapSalaRow, filterSalasBySearch, withClienteColumn } from '../utils/salas.utils';
import { showErrorToast } from '../service/toast.service';

const AUTO_UPDATE_MS = 10000;

const TABLE_COLUMNS = [
  { key: 'protocolo', label: 'Protocolo' },
  { key: 'dataCriacao', label: 'Data de criação' },
  { key: 'horarioCriacao', label: 'Horário de criação' },
  { key: 'status', label: 'Status atual' },
  { key: 'primario', label: 'Primário' },
  { key: 'nomePrimario', label: 'Nome primário' },
  { key: 'secundario', label: 'Secundário' },
  { key: 'nomeSecundario', label: 'Nome secundário' },
  { key: 'duracaoEspera', label: 'Duração da espera' },
  { key: 'duracaoChamada', label: 'Duração da chamada' }
  // { key: 'nivelTransmissaoPrimario', label: 'Nível Transmissão (Primário)' },
  // { key: 'nivelTransmissaoSecundario', label: 'Nível Transmissão (Secundário)' },
  // { key: 'atualizadoEm', label: 'Atualizado em' },
  // { key: 'criadoEm', label: 'Criado em' }
];

export default {
  name: 'SalasTempoReal',

  components: {
    PainelHeader,
    PainelSearchFilter,
    PainelConfirmModal,
    PainelSalasTable
  },

  data() {
    return {
      rows: [],
      clientes: [],
      search: '',
      selectedClienteId: '',
      isAdmin: isAdminCliente(),
      loading: false,
      desativandoId: null,
      loadFailed: false,
      updateInterval: null,
      isRefreshing: false,
      manualRefreshing: false,
      confirmModal: {
        aberto: false,
        mensagem: '',
        row: null
      }
    };
  },

  computed: {
    tableColumns() {
      return withClienteColumn(TABLE_COLUMNS, this.isAdmin);
    },

    filteredRows() {
      return filterSalasBySearch(this.rows, this.search);
    }
  },

  mounted() {
    if (this.isAdmin) {
      this.loadClientes();
    }

    this.loadSalas();
    this.startAutoUpdate();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },

  beforeDestroy() {
    this.stopAutoUpdate();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },

  methods: {
    goBack() {
      this.$router.push('/painel/dashboard');
    },

    async loadClientes() {
      try {
        this.clientes = await listarClientes();
      } catch (err) {
        showErrorToast('Não foi possível carregar a lista de clientes.');
      }
    },

    onClienteChange() {
      this.loadSalas();
    },

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoUpdate();
        return;
      }

      this.loadSalas(true);
      this.startAutoUpdate();
    },

    async loadSalas(isAutoUpdate = false) {
      if (isAutoUpdate && this.isRefreshing) {
        return;
      }

      if (!isAutoUpdate) {
        this.loading = true;
      } else {
        this.isRefreshing = true;
      }

      this.loadFailed = false;

      const clienteId = getClienteId();
      if (!clienteId) {
        logout();
        this.$router.push('/painel');
        return;
      }

      try {
        this.rows = await this.fetchSalas(clienteId);
      } catch (err) {
        if (!isAutoUpdate) {
          showErrorToast('Não foi possível carregar as salas. Tente novamente em instantes.');
          this.loadFailed = true;
        }
        this.rows = [];
      } finally {
        this.loading = false;
        this.isRefreshing = false;
      }
    },

    async fetchSalas(clienteId) {
      let salas;

      if (this.isAdmin) {
        salas = this.selectedClienteId
          ? await salasService.findActiveByCliente(this.selectedClienteId)
          : await salasService.findAllActive();
      } else {
        salas = await salasService.findActiveByCliente(clienteId);
      }

      return (salas || []).map(mapSalaRow);
    },

    async refreshSalas() {
      if (this.loading || this.isRefreshing) {
        return;
      }

      const clienteId = getClienteId();
      if (!clienteId) {
        logout();
        this.$router.push('/painel');
        return;
      }

      this.isRefreshing = true;
      this.manualRefreshing = true;

      try {
        this.rows = await this.fetchSalas(clienteId);
        this.loadFailed = false;
      } catch (err) {
        showErrorToast('Não foi possível atualizar as salas. Tente novamente em instantes.');
      } finally {
        this.isRefreshing = false;
        this.manualRefreshing = false;
        this.startAutoUpdate();
      }
    },

    startAutoUpdate() {
      this.stopAutoUpdate();
      this.updateInterval = setInterval(() => {
        if (!document.hidden) {
          this.loadSalas(true);
        }
      }, AUTO_UPDATE_MS);
    },

    stopAutoUpdate() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    },

    handleDesativar(row) {
      if (!row.ativo || !row.id) {
        return;
      }

      this.confirmModal = {
        aberto: true,
        mensagem: `Desativar a sala "${row.protocolo}"?`,
        row
      };
    },

    fecharConfirmModal() {
      if (this.desativandoId !== null) {
        return;
      }

      this.resetConfirmModal();
    },

    resetConfirmModal() {
      this.confirmModal = {
        aberto: false,
        mensagem: '',
        row: null
      };
    },

    async confirmarDesativar() {
      const row = this.confirmModal.row;

      if (!row || !row.id) {
        this.resetConfirmModal();
        return;
      }

      this.desativandoId = row.id;

      try {
        await salasService.deactivate(row.id);
        this.rows = this.rows.filter((item) => item.id !== row.id);
        this.resetConfirmModal();
        await this.loadSalas();
      } catch (err) {
        showErrorToast('Não foi possível desativar a sala. Tente novamente.');
      } finally {
        this.desativandoId = null;
      }
    }
  }
};
</script>

<style scoped src="./SalasTempoReal.css"></style>
